/**
 * When a selected brand kit may overwrite the subtitle, logo and layout state
 * ("prefill"). Shared by the editor and both clips pages, which restore the
 * project/library's saved settings (`subtitle_config`) on load.
 */
export interface KitPrefillGate {
  /** Saved settings were restored; a kit picked automatically must not overwrite them. */
  keepSavedSettings: boolean;
  /** Kit whose settings are already in the editor; settling on it again is a no-op. */
  prefilledKitId: string | null;
}

export const OPEN_KIT_PREFILL_GATE: KitPrefillGate = {
  keepSavedSettings: false,
  prefilledKitId: null,
};

/** The gate right after the page restored (or found no) saved settings. */
export function gateForSavedSettings({
  hasSavedSettings,
  savedKitId,
}: {
  hasSavedSettings: boolean;
  savedKitId: string | null | undefined;
}): KitPrefillGate {
  return hasSavedSettings
    ? { keepSavedSettings: true, prefilledKitId: savedKitId ?? null }
    : OPEN_KIT_PREFILL_GATE;
}

/**
 * A kit became the selection without an explicit pick: the saved kit being
 * restored, a fallback kit, or a kit restored once the plan is known. Saved
 * settings survive every such pick (the PRO answer often arrives after a
 * fallback kit settled); only an applied kit releases them.
 */
export function settleSelectedKit(
  gate: KitPrefillGate,
  kitId: string,
): { gate: KitPrefillGate; prefill: boolean } {
  if (gate.keepSavedSettings) {
    return { gate: { keepSavedSettings: true, prefilledKitId: kitId }, prefill: false };
  }
  if (gate.prefilledKitId === kitId) return { gate, prefill: false };
  return { gate: { keepSavedSettings: false, prefilledKitId: kitId }, prefill: true };
}

/** The user (or the assistant) applied a kit; its settings are in the editor now. */
export function gateForAppliedKit(kitId: string): KitPrefillGate {
  return { keepSavedSettings: false, prefilledKitId: kitId };
}
